<template>
  <div class="bloque-datos" ref="rootEl">
    <!-- TEXTO libre (caja de texto editable por el usuario) -->
    <div
      v-if="tipo === 'TEXTO'"
      class="bloque-texto ql-editor"
      v-html="params?.html || '<p><em>Caja de texto vacía — haga clic en Editar para redactar.</em></p>'"
    ></div>

    <ComparativoSeccion
      v-else-if="tipo === 'COMPARATIVO'"
      :idEjecucion="params?.id_ejecucion"
      :vistaInicial="params?.vista"
      :filtroComponentes="params?.filtroComponentes"
      :soloLectura="modoPreview"
      :ocultarNivel="true"
      @update:vistaInicial="emit('update:vistaInicial', $event)"
    />

    <!-- IMAGEN -->
    <div v-else-if="tipo === 'IMAGEN'" class="bloque-imagen">
      <img
        v-if="params?.url"
        :src="params.url"
        :alt="params?.alt ?? ''"
        class="imagen-bloque"
        :style="{ width: params?.ancho ?? '100%' }"
      />
      <p v-if="params?.titulo" class="imagen-titulo">{{ params.titulo }}</p>
      <p v-if="!params?.url" class="sin-datos">Sin imagen — haga clic en Opciones para configurar.</p>
    </div>

    <!-- TABLA_CUSTOM -->
    <div v-else-if="tipo === 'TABLA_CUSTOM'">
      <p v-if="params?.titulo" class="tabla-custom-titulo">{{ params.titulo }}</p>
      <div v-if="params?.headers?.length" class="contenedor-tabla">
        <table class="lista-tabla">
          <thead>
            <tr>
              <th v-for="(h, i) in params.headers" :key="i">{{ h || '—' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, ri) in (params.rows ?? [])" :key="ri">
              <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
            </tr>
            <tr v-if="!(params.rows ?? []).length">
              <td :colspan="params.headers.length" class="sin-datos">Sin filas.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="sin-datos">Sin tabla — haga clic en Opciones para configurar.</p>
    </div>

    <p v-else-if="!datos" class="sin-datos">Datos de resultados no disponibles.</p>

    <!-- KPIS_RESUMEN -->
    <div v-else-if="tipo === 'KPIS_RESUMEN'" class="kpis-grid">
      <div v-if="kpiVisible('total')" class="kpi-card">
        <span class="kpi-label">Total indicadores</span>
        <span class="kpi-valor">{{ datos.resumen.aspectos.total }}</span>
      </div>
      <div v-if="kpiVisible('evaluados')" class="kpi-card">
        <span class="kpi-label">Evaluados</span>
        <span class="kpi-valor">{{ evaluados }}</span>
      </div>
      <div v-if="kpiVisible('promedio')" class="kpi-card">
        <span class="kpi-label">Promedio general</span>
        <span class="kpi-valor" :class="`clas-${datos.global?.clasificacion ?? 'no_aplica'}`">
          {{ fmtVal(datos.global?.valoracion) }}
        </span>
      </div>
      <div v-if="kpiVisible('nivel')" class="kpi-card">
        <span class="kpi-label">Nivel general</span>
        <span class="kpi-valor">{{ datos.global?.grado ?? '—' }}</span>
      </div>
    </div>

    <!-- TABLA_GENERAL -->
    <div v-else-if="tipo === 'TABLA_GENERAL'" class="contenedor-tabla">
      <table class="lista-tabla">
        <thead>
          <tr>
            <th v-if="col('n_factor')">N°</th>
            <th v-if="col('nombre_factor')">Factor</th>
            <th v-if="col('peso_factor')">Ponderación</th>
            <th v-if="col('n_caract')">N° C.</th>
            <th v-if="col('nombre_caract')">Característica</th>
            <th v-if="col('n_aspecto')">N° A.</th>
            <th v-if="col('aspecto')">Aspecto</th>
            <th v-if="col('valoracion')" class="col-num">Valoración</th>
            <th v-if="col('gradacion')" class="col-num">Gradación</th>
            <th v-if="col('pct')" class="col-num">% Prom. Pond.</th>
            <th v-if="col('nivel')">Nivel de Cumplimiento</th>
            <th v-for="ec in extraCols" :key="ec.id" class="col-extra">{{ ec.titulo }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="fila in filasVista" :key="fila.key">
            <tr v-if="fila.tipo === 'aspecto'" class="fila-aspecto">
              <td v-if="col('n_factor') && fila.mostrarFactor"     :rowspan="fila.factorRowspan" class="celda-factor">{{ fmtOrdinal(fila.factor.ordinal) }}</td>
              <td v-if="col('nombre_factor') && fila.mostrarFactor" :rowspan="fila.factorRowspan" class="celda-factor nombre">{{ fila.factor.nombre }}</td>
              <td v-if="col('peso_factor') && fila.mostrarFactor"   :rowspan="fila.factorRowspan" class="celda-factor col-num">{{ fmtPeso(fila.factor.peso) }}</td>
              <template v-if="fila.caract">
                <td v-if="col('n_caract') && fila.mostrarCaract"     :rowspan="fila.caractRowspan">{{ fmtOrdinal(fila.caract.ordinal) }}</td>
                <td v-if="col('nombre_caract') && fila.mostrarCaract" :rowspan="fila.caractRowspan" class="nombre">{{ fila.caract.nombre }}</td>
              </template>
              <template v-else>
                <td v-if="col('n_caract')"></td>
                <td v-if="col('nombre_caract')" class="nombre">—</td>
              </template>
              <td v-if="col('n_aspecto')">{{ fmtOrdinal(fila.aspecto?.ordinal) }}</td>
              <td v-if="col('aspecto')" class="nombre">{{ fila.aspecto?.nombre ?? '—' }}</td>
              <td v-if="col('valoracion')" class="col-num">{{ fmtVal(fila.aspecto?.valoracion ?? null) }}</td>
              <td v-if="col('gradacion') && fila.caract && fila.mostrarCaract" :rowspan="fila.caractRowspan" class="col-num">{{ fmtPeso(fila.caract.peso) }}</td>
              <td v-else-if="col('gradacion') && !fila.caract" class="col-num">—</td>
              <td v-if="col('pct') && fila.caract && fila.mostrarCaract" :rowspan="fila.caractRowspan" class="col-num">{{ fmtVal(fila.caract.valoracion) }}</td>
              <td v-else-if="col('pct') && !fila.caract" class="col-num">—</td>
              <td v-if="col('nivel')" class="nivel" :class="`clas-${fila.aspecto?.clasificacion ?? 'no_aplica'}`">{{ fila.aspecto?.grado ?? '—' }}</td>
              <!-- Columnas extra: una celda por factor con rowspan -->
              <template v-for="ec in extraCols" :key="ec.id">
                <td v-if="fila.mostrarFactor" :rowspan="fila.factorRowspan" class="col-extra">
                  {{ ec.filas[`f_${fila.factor.id_componente}`] ?? '' }}
                </td>
              </template>
            </tr>
            <tr v-else class="fila-subtotal">
              <td :colspan="colSpanSubtotal">SUBTOTAL FACTOR {{ fmtOrdinal(fila.factor.ordinal) }} · {{ fila.factor.nombre }}</td>
              <td v-if="col('valoracion')" class="col-num">{{ fmtVal(fila.factor.valoracion) }}</td>
              <td v-if="col('gradacion')" class="col-num">{{ fmtPeso(fila.factor.peso) }}</td>
              <td v-if="col('pct')" class="col-num">{{ fmtPct(fila.factor.pct) }}</td>
              <td v-if="col('nivel')" class="nivel" :class="`clas-${fila.factor.clasificacion}`">{{ fila.factor.grado ?? '—' }}</td>
              <td v-for="ec in extraCols" :key="ec.id"></td>
            </tr>
          </template>
          <tr v-if="datos.global" class="fila-global">
            <td :colspan="colSpanSubtotal">CALIFICACIÓN GLOBAL DEL MODELO</td>
            <td v-if="col('valoracion')" class="col-num">{{ fmtVal(datos.global.valoracion) }}</td>
            <td v-if="col('gradacion')" class="col-num">100%</td>
            <td v-if="col('pct')" class="col-num">{{ fmtPct(datos.global.pct) }}</td>
            <td v-if="col('nivel')" class="nivel" :class="`clas-${datos.global.clasificacion}`">{{ datos.global.grado ?? '—' }}</td>
            <td v-for="ec in extraCols" :key="ec.id"></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- TABLA_SEMAFORO -->
    <div v-else-if="tipo === 'TABLA_SEMAFORO'">
      <div class="resumen-cards">
        <div v-for="(r, k) in datos.resumen" :key="k" class="resumen-card">
          <span class="resumen-titulo">{{ nivelLabel(String(k)) }}</span>
          <div class="resumen-fila"><span class="pt fuerte"></span> Fuertes <b>{{ r.fuerte }}</b> <small>({{ pctResumen(r.fuerte, r.total) }})</small></div>
          <div class="resumen-fila"><span class="pt debil"></span> Débiles <b>{{ r.debil }}</b> <small>({{ pctResumen(r.debil, r.total) }})</small></div>
          <div class="resumen-fila"><span class="pt muy_debil"></span> Muy débiles <b>{{ r.muy_debil }}</b> <small>({{ pctResumen(r.muy_debil, r.total) }})</small></div>
          <div class="resumen-fila"><span class="pt no_aplica"></span> No aplica <b>{{ r.no_aplica }}</b></div>
        </div>
      </div>
      <div class="contenedor-tabla">
        <table class="lista-tabla">
          <thead>
            <tr>
              <th v-if="col('caract')">Característica</th>
              <th v-if="col('aspecto')">Aspecto</th>
              <th v-if="col('valoracion')" class="col-num">Valoración</th>
              <th v-if="col('fuerte')"   class="col-sem">Fuerte</th>
              <th v-if="col('debil')"    class="col-sem">Débil</th>
              <th v-if="col('muy_debil')" class="col-sem">Muy débil</th>
              <th v-for="ec in extraCols" :key="ec.id" class="col-extra">{{ ec.titulo }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="grupo in gruposSemaforo" :key="grupo.id">
              <tr v-if="col('caract')" class="fila-caract-head">
                <td :colspan="colSpanSemaforo">{{ fmtOrdinal(grupo.ordinal) }} {{ grupo.nombre }}</td>
              </tr>
              <tr v-for="a in grupo.hojas" :key="grupo.id + '-' + a.id_componente">
                <td v-if="col('caract')"></td>
                <td v-if="col('aspecto')" class="nombre">{{ (a.ordinal ? fmtOrdinal(a.ordinal) + '. ' : '') + a.nombre }}</td>
                <td v-if="col('valoracion')" class="col-num">{{ fmtVal(a.valoracion) }}</td>
                <td v-if="col('fuerte')"    class="col-sem"><span v-if="a.clasificacion === 'fuerte'" class="marca fuerte"></span></td>
                <td v-if="col('debil')"     class="col-sem"><span v-if="a.clasificacion === 'debil'" class="marca debil"></span></td>
                <td v-if="col('muy_debil')" class="col-sem"><span v-if="a.clasificacion === 'muy_debil'" class="marca muy_debil"></span></td>
                <td v-for="ec in extraCols" :key="ec.id" class="col-extra">
                  {{ ec.filas[`c_${a.id_componente}`] ?? '' }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- TABLA_ESTRUCTURA -->
    <div v-else-if="tipo === 'TABLA_ESTRUCTURA'" class="contenedor-tabla">
      <table class="lista-tabla estructura">
        <thead>
          <tr>
            <th v-if="col('codigo')">Código</th>
            <th v-if="col('tipo')">Tipo</th>
            <th v-if="col('nombre')">Nombre</th>
            <th v-if="col('peso')"        class="col-num">Ponderación</th>
            <th v-if="col('valoracion')"  class="col-num">Valoración</th>
            <th v-if="col('pct')"         class="col-num">%</th>
            <th v-if="col('grado')">Grado</th>
            <th v-if="col('clasificacion')">Clasificación</th>
            <th v-for="ec in extraCols" :key="ec.id" class="col-extra">{{ ec.titulo }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="f in filasEstructura" :key="f.key" :class="'nivel-' + f.tipoNivel">
            <td v-if="col('codigo')" class="cod">{{ fmtOrdinal(f.codigo) }}</td>
            <td v-if="col('tipo')">{{ f.tipo }}</td>
            <td v-if="col('nombre')" class="nombre">{{ f.nombre }}</td>
            <td v-if="col('peso')"        class="col-num">{{ f.peso === null ? '—' : fmtPeso(f.peso) }}</td>
            <td v-if="col('valoracion')"  class="col-num">{{ fmtVal(f.valoracion) }}</td>
            <td v-if="col('pct')"         class="col-num">{{ fmtPct(f.pct) }}</td>
            <td v-if="col('grado')">{{ f.grado ?? '—' }}</td>
            <td v-if="col('clasificacion')"><span class="badge-clas" :class="`clas-${f.clasificacion}`">{{ clasLabel(f.clasificacion) }}</span></td>
            <td v-for="ec in extraCols" :key="ec.id" class="col-extra">
              {{ ec.filas[f.stableKey] ?? '' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- GRAFICOS_TODOS -->
    <div v-else-if="tipo === 'GRAFICOS_TODOS'" class="graficos">
      <div v-for="g in graficosVisibles" :key="g.id" class="grafico-card" :style="{ width: anchoGrafico + 'px' }">
        <p class="grafico-titulo">Factor {{ fmtOrdinal(g.ordinal) }}: {{ g.nombre }}</p>
        <svg :viewBox="`0 0 ${g.width} ${svgH}`" class="grafico-svg" preserveAspectRatio="xMidYMid meet">
          <line x1="38" y1="10" x2="38" :y2="svgH - 40" stroke="#ccc" />
          <line x1="38" :y1="svgH - 40" :x2="g.width - 10" :y2="svgH - 40" stroke="#ccc" />
          <template v-for="t in ejeTicks" :key="'t' + t">
            <line x1="34" :y1="yPos(t)" x2="38" :y2="yPos(t)" stroke="#999" />
            <text x="30" :y="yPos(t) + 3" text-anchor="end" class="eje-label">{{ t }}</text>
          </template>
          <g v-for="(b, i) in g.barras" :key="'b' + i">
            <rect :x="48 + i * g.paso" :y="yPos(b.valor ?? 0)"
              :width="g.anchoBarra" :height="svgH - 40 - yPos(b.valor ?? 0)"
              :fill="clasColor(b.clasificacion)" rx="2" />
            <text :x="48 + i * g.paso + g.anchoBarra / 2" :y="yPos(b.valor ?? 0) - 4"
              text-anchor="middle" class="barra-val">{{ b.valor === null ? '—' : b.valor.toFixed(2) }}</text>
            <text :x="48 + i * g.paso + g.anchoBarra / 2" :y="svgH - 26"
              text-anchor="middle" class="barra-label">{{ fmtOrdinal(b.ordinal) }}</text>
          </g>
        </svg>
      </div>
    </div>

    <!-- GRAFICO_FACTOR -->
    <div v-else-if="tipo === 'GRAFICO_FACTOR'">
      <div v-if="graficoFactor" class="grafico-card" :style="{ width: anchoGrafico + 'px' }">
        <p class="grafico-titulo">Factor {{ fmtOrdinal(graficoFactor.ordinal) }}: {{ graficoFactor.nombre }}</p>
        <svg :viewBox="`0 0 ${graficoFactor.width} ${svgH}`" class="grafico-svg" preserveAspectRatio="xMidYMid meet">
          <line x1="38" y1="10" x2="38" :y2="svgH - 40" stroke="#ccc" />
          <line x1="38" :y1="svgH - 40" :x2="graficoFactor.width - 10" :y2="svgH - 40" stroke="#ccc" />
          <template v-for="t in ejeTicks" :key="'t' + t">
            <line x1="34" :y1="yPos(t)" x2="38" :y2="yPos(t)" stroke="#999" />
            <text x="30" :y="yPos(t) + 3" text-anchor="end" class="eje-label">{{ t }}</text>
          </template>
          <g v-for="(b, i) in graficoFactor.barras" :key="'b' + i">
            <rect :x="48 + i * graficoFactor.paso" :y="yPos(b.valor ?? 0)"
              :width="graficoFactor.anchoBarra" :height="svgH - 40 - yPos(b.valor ?? 0)"
              :fill="clasColor(b.clasificacion)" rx="2" />
            <text :x="48 + i * graficoFactor.paso + graficoFactor.anchoBarra / 2" :y="yPos(b.valor ?? 0) - 4"
              text-anchor="middle" class="barra-val">{{ b.valor === null ? '—' : b.valor.toFixed(2) }}</text>
            <text :x="48 + i * graficoFactor.paso + graficoFactor.anchoBarra / 2" :y="svgH - 26"
              text-anchor="middle" class="barra-label">{{ fmtOrdinal(b.ordinal) }}</text>
          </g>
        </svg>
      </div>
      <p v-else class="sin-datos">Factor no encontrado.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Bloque de datos embebido en el informe.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useGestionResultadosAutoevaluacion } from '@/store/GestionResultadosAutoevaluacion'
import type { NodoResultado, Clasificacion } from '@/interfaces/ResultadosAutoevaluacion'
import ComparativoSeccion from '@/components/ComparativoSeccion.vue'

const rootEl = ref<HTMLElement | null>(null)

const drag = { pending: false, active: false, el: null as HTMLElement | null, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 }
const DRAG_THRESHOLD = 4

function findScrollable(target: EventTarget | null): HTMLElement | null {
  let node = target as Element | null
  while (node && node !== rootEl.value) {
    if (node instanceof HTMLElement && node.classList.contains('contenedor-tabla')) return node
    node = node.parentElement
  }
  return null
}

function onDragStart(e: MouseEvent) {
  if (e.button !== 0) return
  const el = findScrollable(e.target)
  if (!el) return
  drag.pending = true
  drag.active = false
  drag.el = el
  drag.startX = e.pageX
  drag.startY = e.pageY
  drag.scrollLeft = el.scrollLeft
  drag.scrollTop = el.scrollTop
}

function onDragMove(e: MouseEvent) {
  if (!drag.el) return
  const dx = e.pageX - drag.startX
  const dy = e.pageY - drag.startY
  if (!drag.active) {
    if (!drag.pending || (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD)) return
    drag.active = true
    drag.pending = false
    drag.el.style.cursor = 'grabbing'
    drag.el.style.userSelect = 'none'
  }
  e.preventDefault()
  drag.el.scrollLeft = drag.scrollLeft - dx
  drag.el.scrollTop  = drag.scrollTop  - dy
}

function onDragEnd() {
  if (!drag.el) return
  drag.el.style.cursor = ''
  drag.el.style.userSelect = ''
  drag.active = false
  drag.pending = false
  drag.el = null
}

onMounted(() => {
  rootEl.value?.addEventListener('mousedown', onDragStart)
  window.addEventListener('mousemove', onDragMove)
  window.addEventListener('mouseup', onDragEnd)
})

onUnmounted(() => {
  rootEl.value?.removeEventListener('mousedown', onDragStart)
  window.removeEventListener('mousemove', onDragMove)
  window.removeEventListener('mouseup', onDragEnd)
})

const props = defineProps<{
  tipo: string
  params?: Record<string, any>
  modoPreview?: boolean
}>()

const emit = defineEmits<{ (e: 'update:vistaInicial', v: 'tabla' | 'grafico'): void }>()

const store = useGestionResultadosAutoevaluacion()
const datos = computed(() => store.resultados)
const escala = computed(() => datos.value?.escala_max ?? 5)
const factores = computed(() => datos.value?.factores ?? [])

// ── Params helpers ────────────────────────────────────────────────────────────

interface ExtraColumna { id: string; titulo: string; filas: Record<string, string> }

/** Columna del sistema visible (o todas si no hay config) */
function col(clave: string): boolean {
  const cols = props.params?.columnas as string[] | undefined
  return !cols || cols.includes(clave)
}

/** KPI card visible */
function kpiVisible(clave: string): boolean {
  const t = props.params?.tarjetas as string[] | undefined
  return !t || t.includes(clave)
}

const extraCols = computed<ExtraColumna[]>(() => props.params?.columnasExtra ?? [])

/** Altura SVG para gráficos */
const svgH = computed<number>(() =>
  ({ sm: 150, md: 200, lg: 280 }[props.params?.altura as string ?? 'md'] ?? 200)
)

/** Ancho máximo de cada tarjeta de gráfico en px */
const anchoGrafico = computed<number>(() =>
  ({ sm: 300, md: 420, lg: 560 }[props.params?.ancho as string ?? 'md'] ?? 420)
)

// ── Helpers de formato ────────────────────────────────────────────────────────

const evaluados = computed(() => {
  const r = datos.value?.resumen.aspectos
  if (!r) return '—'
  return r.fuerte + r.debil + r.muy_debil
})

function fmtOrdinal(ordinal: string | null | undefined): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}
function fmtVal(v: number | null | undefined): string {
  return v == null ? '—' : v.toFixed(2)
}
function fmtPct(v: number | null | undefined): string {
  return v == null ? '—' : `${v.toFixed(1)}%`
}
function fmtPeso(v: number | null | undefined): string {
  if (v == null) return '—'
  return `${(+v).toFixed(v % 1 === 0 ? 0 : 2)}%`
}
function clasLabel(c: Clasificacion): string {
  return { fuerte: 'Fuerte', debil: 'Débil', muy_debil: 'Muy débil', no_aplica: 'No aplica' }[c]
}
function clasColor(c: Clasificacion): string {
  return { fuerte: '#28a745', debil: '#ffc107', muy_debil: '#dc3545', no_aplica: '#c9c9c9' }[c]
}
function nivelLabel(k: string): string {
  return ({ factores: 'Factores', caracteristicas: 'Características', aspectos: 'Aspectos' } as Record<string, string>)[k] ?? k
}
function pctResumen(n: number, total: number): string {
  return total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '0%'
}
function recolectarHojas(nodo: NodoResultado): NodoResultado[] {
  if (nodo.es_hoja || !nodo.hijos.length) return [nodo]
  return nodo.hijos.flatMap(recolectarHojas)
}

// ── TABLA_GENERAL ─────────────────────────────────────────────────────────────

interface FilaGeneral {
  key: string; tipo: 'aspecto' | 'subtotal'; factor: NodoResultado
  factorRowspan?: number; mostrarFactor?: boolean
  caract?: NodoResultado; caractRowspan?: number; mostrarCaract?: boolean
  aspecto?: NodoResultado | null
}

const filasGenerales = computed<FilaGeneral[]>(() => {
  const filas: FilaGeneral[] = []
  let n = 0
  for (const factor of factores.value) {
    const caracts = factor.hijos.length ? factor.hijos : [factor]
    const grupos = caracts.map(c => {
      const hojas = recolectarHojas(c)
      return { nodo: c, hojas: hojas.length ? hojas : [null] as (NodoResultado | null)[] }
    })
    const totalFactor = grupos.reduce((s, g) => s + g.hojas.length, 0) || 1
    let primeraFactor = true
    for (const g of grupos) {
      let primeraCaract = true
      for (const hoja of g.hojas) {
        filas.push({
          key: `a${n++}`, tipo: 'aspecto', factor,
          factorRowspan: totalFactor, mostrarFactor: primeraFactor,
          caract: g.nodo === factor ? undefined : g.nodo,
          caractRowspan: g.hojas.length, mostrarCaract: primeraCaract, aspecto: hoja
        })
        primeraFactor = false; primeraCaract = false
      }
    }
    filas.push({ key: `s${factor.id_componente}`, tipo: 'subtotal', factor })
  }
  return filas
})

// Sliced rendering for paginated preview
function reindexarFilas(filas: FilaGeneral[]): FilaGeneral[] {
  const result: FilaGeneral[] = []
  let fi = 0
  while (fi < filas.length) {
    const fId = filas[fi].factor.id_componente
    let fj = fi
    while (fj < filas.length && filas[fj].factor.id_componente === fId) fj++
    const fRows = filas.slice(fi, fj)
    let ci = 0
    while (ci < fRows.length) {
      const cId = fRows[ci].caract?.id_componente ?? -1
      let cj = ci
      while (cj < fRows.length && (fRows[cj].caract?.id_componente ?? -1) === cId) cj++
      const cRows = fRows.slice(ci, cj)
      for (let ri = 0; ri < cRows.length; ri++) {
        result.push({
          ...cRows[ri],
          factorRowspan: fRows.length,
          mostrarFactor: ci === 0 && ri === 0,
          caractRowspan: cRows.length,
          mostrarCaract: ri === 0
        })
      }
      ci = cj
    }
    fi = fj
  }
  return result
}

const filasVista = computed<FilaGeneral[]>(() => {
  const slice = props.params?.rowSlice as { start: number; end: number } | undefined
  if (!slice) return filasGenerales.value
  const aspectos = filasGenerales.value.filter(f => f.tipo === 'aspecto')
  return reindexarFilas(aspectos.slice(slice.start, slice.end))
})

// Colspan para filas de subtotal/global en TABLA_GENERAL
const colSpanSubtotal = computed(() => {
  let n = 0
  if (col('n_factor'))      n++
  if (col('nombre_factor')) n++
  if (col('peso_factor'))   n++
  if (col('n_caract'))      n++
  if (col('nombre_caract')) n++
  if (col('n_aspecto'))     n++
  if (col('aspecto'))       n++
  return n || 1
})

// ── TABLA_SEMAFORO ────────────────────────────────────────────────────────────

const gruposSemaforo = computed(() => {
  const grupos: { id: number; ordinal: string | null; nombre: string; hojas: NodoResultado[] }[] = []
  for (const factor of factores.value) {
    const caracts = factor.hijos.length ? factor.hijos : [factor]
    for (const c of caracts) {
      const hojas = recolectarHojas(c)
      if (hojas.length) grupos.push({ id: c.id_componente, ordinal: c.ordinal, nombre: c.nombre, hojas })
    }
  }
  return grupos
})

const colSpanSemaforo = computed(() => {
  let n = 0
  if (col('caract'))     n++
  if (col('aspecto'))    n++
  if (col('valoracion')) n++
  if (col('fuerte'))     n++
  if (col('debil'))      n++
  if (col('muy_debil'))  n++
  return (n || 1) + extraCols.value.length
})

// ── TABLA_ESTRUCTURA ──────────────────────────────────────────────────────────

interface FilaEstructura {
  key: string; stableKey: string; codigo: string; tipo: string; tipoNivel: number
  nombre: string; peso: number | null; valoracion: number | null; pct: number | null
  grado: string | null; clasificacion: Clasificacion
}

const filasEstructura = computed<FilaEstructura[]>(() => {
  const filas: FilaEstructura[] = []
  let n = 0
  const tipoPorNivel = (nivel: number) => nivel === 1 ? 'Factor' : nivel === 2 ? 'Característica' : 'Subnivel'
  const walk = (nodo: NodoResultado) => {
    if (nodo.es_hoja && nodo.aspectos.length <= 1) {
      filas.push({ key: `e${n++}`, stableKey: `c_${nodo.id_componente}`, codigo: nodo.ordinal ?? '', tipo: 'Aspecto', tipoNivel: 3, nombre: nodo.nombre, peso: nodo.peso, valoracion: nodo.valoracion, pct: nodo.pct, grado: nodo.grado, clasificacion: nodo.clasificacion })
      return
    }
    filas.push({ key: `e${n++}`, stableKey: `c_${nodo.id_componente}`, codigo: nodo.ordinal ?? '', tipo: tipoPorNivel(nodo.nivel), tipoNivel: Math.min(nodo.nivel, 3), nombre: nodo.nombre, peso: nodo.peso, valoracion: nodo.valoracion, pct: nodo.pct, grado: nodo.grado, clasificacion: nodo.clasificacion })
    for (const a of nodo.aspectos) {
      filas.push({ key: `e${n++}`, stableKey: `a_${a.id_indicador}`, codigo: a.ordinal ?? '', tipo: 'Aspecto', tipoNivel: 3, nombre: a.nombre, peso: null, valoracion: a.valoracion, pct: a.pct, grado: a.grado, clasificacion: a.clasificacion })
    }
    for (const h of nodo.hijos) walk(h)
  }
  for (const f of factores.value) walk(f)
  return filas
})

// ── GRÁFICOS ──────────────────────────────────────────────────────────────────

interface DatoGrafico {
  id: number; ordinal: string | null; nombre: string
  barras: { ordinal: string | null; valor: number | null; clasificacion: Clasificacion }[]
  paso: number; anchoBarra: number; width: number
}

function buildGrafico(f: NodoResultado): DatoGrafico {
  const caracts = f.hijos.length ? f.hijos : [f]
  const barras = caracts.map(c => ({ ordinal: c.ordinal, valor: c.valoracion, clasificacion: c.clasificacion }))
  const paso = 62
  const width = Math.max(240, 60 + barras.length * paso)
  return { id: f.id_componente, ordinal: f.ordinal, nombre: f.nombre, barras, paso, anchoBarra: 38, width }
}

const ejeTicks = computed(() => {
  const max = Math.ceil(escala.value)
  const paso = max <= 5 ? 1 : 2
  const ticks: number[] = []
  for (let t = 0; t <= max; t += paso) ticks.push(t)
  return ticks
})

function yPos(v: number): number {
  const max = escala.value || 5
  const h = svgH.value
  return (h - 40) - (Math.max(0, Math.min(max, v)) / max) * (h - 50)
}

const graficosVisibles = computed<DatoGrafico[]>(() => {
  const ids = props.params?.factoresIds as number[] | undefined
  const lista = factores.value.filter(f => !ids || ids.includes(f.id_componente))
  return lista.map(buildGrafico).filter(g => g.barras.length)
})

const graficoFactor = computed<DatoGrafico | null>(() => {
  const idComp = props.params?.id_componente
  if (!idComp) return null
  const f = factores.value.find(x => x.id_componente === idComp)
  return f ? buildGrafico(f) : null
})
</script>

<style scoped>
.bloque-datos { font-family: var(--font-family); font-size: 0.85rem; }
.sin-datos { color: #aaa; font-style: italic; padding: 0.5rem 0; }
.bloque-texto { font-size: 0.9rem; line-height: 1.7; color: #222; padding: 0; border: none; }

/* KPIs */
.kpis-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 0.75rem; }
.kpi-card { border: 1px solid #e0e0e0; border-radius: 8px; padding: 0.75rem 1rem; text-align: center; display: flex; flex-direction: column; gap: 0.25rem; }
.kpi-label { font-size: 0.72rem; color: #666; text-transform: uppercase; letter-spacing: 0.03em; }
.kpi-valor { font-size: 1.4rem; font-weight: 700; color: #1a1a1a; }

/* Tabla base */
.contenedor-tabla { overflow-x: auto; max-width: 100%; width: 100%; cursor: grab; }
.contenedor-tabla:active { cursor: grabbing; }
.lista-tabla { width: 100%; border-collapse: collapse; font-size: 0.82rem; }
.lista-tabla th, .lista-tabla td { padding: 7px 9px; text-align: left; border: 1px solid #e0e0e0; vertical-align: middle; }
.lista-tabla thead { background-color: #0e69a0; color: white; text-transform: uppercase; font-size: 0.68rem; letter-spacing: 0.03em; }
.lista-tabla thead th { border-color: #0b587f; }
.col-num { text-align: right; white-space: nowrap; }
.col-sem { text-align: center; width: 80px; }
.col-extra { background: #fafbfc; font-style: italic; min-width: 120px; white-space: pre-wrap; word-break: break-word; }
thead .col-extra { font-style: normal; background: #0b5580; }
.nombre { font-weight: 500; }
.celda-factor { background: #f5f9fc; font-weight: 600; }
.fila-subtotal td { background: #eaf5e9; font-weight: 700; font-size: 0.78rem; }
.fila-global td { background: #dcefff; font-weight: 700; border-top: 2px solid #0e69a0; }
.nivel { font-weight: 600; font-size: 0.75rem; }
.clas-fuerte { background: #d4edda; color: #155724; }
.clas-debil { background: #fff3cd; color: #856404; }
.clas-muy_debil { background: #f8d7da; color: #721c24; }
.clas-no_aplica { background: #f0f0f0; color: #888; }

/* Semáforo */
.resumen-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
.resumen-card { border: 1px solid #e0e0e0; border-radius: 8px; padding: 0.75rem 1rem; }
.resumen-titulo { font-weight: 700; font-size: 0.85rem; display: block; margin-bottom: 0.4rem; color: #0e69a0; }
.resumen-fila { display: flex; align-items: center; gap: 0.4rem; font-size: 0.8rem; margin-bottom: 0.15rem; }
.resumen-fila small { color: #888; }
.pt { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }
.pt.fuerte { background: #28a745; }
.pt.debil { background: #ffc107; }
.pt.muy_debil { background: #dc3545; }
.pt.no_aplica { background: #c9c9c9; }
.fila-caract-head td { background: #f5f5f5; font-weight: 700; font-size: 0.78rem; }
.marca { width: 14px; height: 14px; border-radius: 3px; display: inline-block; }
.marca.fuerte { background: #28a745; }
.marca.debil { background: #ffc107; }
.marca.muy_debil { background: #dc3545; }

/* Estructura */
.estructura .nivel-1 td { background: #eef5fb; font-weight: 700; }
.estructura .nivel-1 .nombre { color: #0e69a0; }
.estructura .nivel-2 td { background: #f7fbfe; font-weight: 600; }
.estructura .cod { white-space: nowrap; color: #0e69a0; font-weight: 600; }
.badge-clas { display: inline-block; padding: 2px 7px; border-radius: 10px; font-size: 0.7rem; font-weight: 600; }

/* Imagen */
.bloque-imagen { display: flex; flex-direction: column; gap: 0.4rem; }
.imagen-bloque { display: block; max-width: 100%; height: auto; border-radius: 4px; }
.imagen-titulo { font-size: 0.78rem; color: #666; font-style: italic; text-align: center; margin: 0; }

/* Tabla personalizada */
.tabla-custom-titulo { font-size: 0.88rem; font-weight: 700; color: #1a1a1a; margin: 0 0 0.5rem; }

/* Gráficos */
.graficos { display: flex; flex-wrap: wrap; gap: 1rem; }
.grafico-card { border: 1px solid #e0e0e0; border-radius: 8px; padding: 0.75rem 1rem; flex-shrink: 1; min-width: 0; max-width: 100%; box-sizing: border-box; }
.grafico-titulo { font-size: 0.85rem; font-weight: 700; margin: 0 0 0.5rem; color: #0e69a0; }
.grafico-svg { width: 100%; height: auto; }
.eje-label { font-size: 9px; fill: #888; }
.barra-val { font-size: 9px; font-weight: 700; fill: #333; }
.barra-label { font-size: 9px; fill: #555; }
</style>
